import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SmartphoneIcon, CheckCircleIcon, XCircleIcon, AlertTriangleIcon, PlusIcon, SearchIcon, RefreshCwIcon, ChevronDownIcon, BarChart2Icon, CheckIcon } from 'lucide-react';
import { getTenant } from '../../../theme/tokens';
function StatusDropdown({
  value,
  onChange,
  options
}: {
  value: string;
  onChange: (v: string) => void;
  options: Array<{
    value: string;
    label: string;
    dot?: string;
  }>;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const selected = options.find(o => o.value === value);
  return <div ref={ref} className="relative" data-id="element-14019">
      <button type="button" aria-expanded={open} aria-haspopup="listbox" onClick={() => setOpen(!open)} className="h-[42px] px-3 pr-8 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors flex items-center gap-2 min-w-[150px] focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-14020">
        {selected?.dot && <span className={`w-2 h-2 rounded-full ${selected.dot} flex-shrink-0`} data-id="element-14021" />}
        <span className="truncate" data-id="element-14022">{selected?.label}</span>
        <ChevronDownIcon size={14} className={`absolute right-2.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-14023" />
      </button>

      {open && <div className="absolute z-20 mt-1 right-0 w-48 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" data-id="element-14024">
          {options.map(opt => <button key={opt.value} type="button" onClick={() => {
        onChange(opt.value);
        setOpen(false);
      }} className={`w-full px-3 py-2.5 text-left text-sm flex items-center justify-between hover:bg-gray-50 transition-colors ${value === opt.value ? 'bg-[#E2231A]/5 font-medium' : 'text-gray-700'}`} data-id="element-14025">
              <div className="flex items-center gap-2" data-id="element-14026">
                {opt.dot && <span className={`w-2 h-2 rounded-full ${opt.dot} flex-shrink-0`} data-id="element-14027" />}
                <span data-id="element-14028">{opt.label}</span>
              </div>
              {value === opt.value && <CheckIcon size={14} className="text-[#9F140F] flex-shrink-0" data-id="element-14029" />}
            </button>)}
        </div>}
    </div>;
}
interface SoftPosTerminal {
  id: string;
  deviceName: string;
  merchantId: string;
  terminalId: string;
  androidVersion: string;
  status: 'active' | 'inactive' | 'pending';
  lastConnection: string;
  transactionVolume: number;
  transactionCount: number;
  model: string;
  owner: string;
  appVersion: string;
}
const terminals: SoftPosTerminal[] = [{
  id: 'softpos-001',
  deviceName: 'Samsung Galaxy S21',
  merchantId: '123456789012',
  terminalId: 'TID-001234',
  androidVersion: '12',
  status: 'active',
  lastConnection: '5 minutes ago',
  transactionVolume: 8750,
  transactionCount: 127,
  model: 'SM-G991B',
  owner: 'John Smith',
  appVersion: '2.3.1'
}, {
  id: 'softpos-002',
  deviceName: 'Google Pixel 6',
  merchantId: '234567890123',
  terminalId: 'TID-002345',
  androidVersion: '13',
  status: 'active',
  lastConnection: '12 minutes ago',
  transactionVolume: 5230,
  transactionCount: 78,
  model: 'GR1YH',
  owner: 'Sarah Johnson',
  appVersion: '2.3.1'
}, {
  id: 'softpos-003',
  deviceName: 'Xiaomi Mi 11',
  merchantId: '345678901234',
  terminalId: 'TID-003456',
  androidVersion: '11',
  status: 'inactive',
  lastConnection: '2 days ago',
  transactionVolume: 1820,
  transactionCount: 31,
  model: 'M2011K2G',
  owner: 'Michael Chen',
  appVersion: '2.2.5'
}, {
  id: 'softpos-004',
  deviceName: 'Samsung Galaxy Tab S7',
  merchantId: '456789012345',
  terminalId: 'TID-004567',
  androidVersion: '12',
  status: 'active',
  lastConnection: '1 hour ago',
  transactionVolume: 12450,
  transactionCount: 189,
  model: 'SM-T870',
  owner: 'Emma Wilson',
  appVersion: '2.3.0'
}, {
  id: 'softpos-005',
  deviceName: 'OnePlus 9 Pro',
  merchantId: '567890123456',
  terminalId: 'TID-005678',
  androidVersion: '12',
  status: 'pending',
  lastConnection: 'Never',
  transactionVolume: 0,
  transactionCount: 0,
  model: 'LE2121',
  owner: 'David Garcia',
  appVersion: '2.3.1'
}, {
  id: 'softpos-006',
  deviceName: 'Sony Xperia 1 III',
  merchantId: '678901234567',
  terminalId: 'TID-006789',
  androidVersion: '12',
  status: 'active',
  lastConnection: '25 minutes ago',
  transactionVolume: 3560,
  transactionCount: 42,
  model: 'XQ-BC72',
  owner: 'Lisa Taylor',
  appVersion: '2.3.1'
}, {
  id: 'softpos-007',
  deviceName: 'Samsung Galaxy A52',
  merchantId: '789012345678',
  terminalId: 'TID-007890',
  androidVersion: '11',
  status: 'inactive',
  lastConnection: '5 days ago',
  transactionVolume: 980,
  transactionCount: 15,
  model: 'SM-A525F',
  owner: 'Robert Brown',
  appVersion: '2.2.0'
}, {
  id: 'softpos-008',
  deviceName: 'Google Pixel 5',
  merchantId: '890123456789',
  terminalId: 'TID-008901',
  androidVersion: '12',
  status: 'active',
  lastConnection: '3 hours ago',
  transactionVolume: 6740,
  transactionCount: 103,
  model: 'GD1YQ',
  owner: 'Jennifer Martinez',
  appVersion: '2.3.1'
}];
const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors placeholder:text-gray-400';
const getStatusBadge = (status: 'active' | 'inactive' | 'pending') => {
  const styles = {
    active: 'bg-green-50 text-green-700',
    inactive: 'bg-red-50 text-red-700',
    pending: 'bg-amber-50 text-amber-700'
  };
  const icons = {
    active: <CheckCircleIcon size={11} className="mr-1" data-id="element-14030" />,
    inactive: <XCircleIcon size={11} className="mr-1" data-id="element-14031" />,
    pending: <AlertTriangleIcon size={11} className="mr-1" data-id="element-14032" />
  };
  const labels = {
    active: 'Active',
    inactive: 'Inactive',
    pending: 'Pending'
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${styles[status]}`} data-id="element-14033">
      {icons[status]}
      {labels[status]}
    </span>;
};
export function SoftPosTerminals() {
  const {
    t
  } = useTranslation('terminals');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const filteredTerminals = terminals.filter(t => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = t.deviceName.toLowerCase().includes(q) || t.merchantId.includes(searchQuery) || t.terminalId.includes(searchQuery) || t.owner.toLowerCase().includes(q);
    return matchesSearch && (statusFilter === 'all' || t.status === statusFilter);
  });
  const totalDevices = terminals.length;
  const activeDevices = terminals.filter(t => t.status === 'active').length;
  const totalVolume = terminals.reduce((s, t) => s + t.transactionVolume, 0);
  return <div className="space-y-5" data-id="element-14034">
      {/* ─── STATS ─── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-14035">
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-3" data-id="element-14036">
          <div className="h-10 w-10 rounded-lg bg-[#E2231A]/10 flex items-center justify-center flex-shrink-0" data-id="element-14037">
            <SmartphoneIcon size={18} className="text-[#9F140F]" data-id="element-14038" />
          </div>
          <div data-id="element-14039">
            <p className="text-xs text-gray-500" data-id="element-14040">
              {t('pages.SoftPosTerminals.total_devices')}
            </p>
            <p className="text-xl font-bold text-gray-900" data-id="element-14041">{totalDevices}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-3" data-id="element-14042">
          <div className="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0" data-id="element-14043">
            <CheckCircleIcon size={18} className="text-green-600" data-id="element-14044" />
          </div>
          <div data-id="element-14045">
            <p className="text-xs text-gray-500" data-id="element-14046">
              {t('pages.SoftPosTerminals.active_devices')}
            </p>
            <p className="text-xl font-bold text-gray-900" data-id="element-14047">{activeDevices}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-3" data-id="element-14048">
          <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0" data-id="element-14049">
            <BarChart2Icon size={18} className="text-purple-600" data-id="element-14050" />
          </div>
          <div data-id="element-14051">
            <p className="text-xs text-gray-500" data-id="element-14052">
              {t('pages.Connect.total_volume')}
            </p>
            <p className="text-xl font-bold text-gray-900" data-id="element-14053">
              {getTenant().currency.code} {totalVolume.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* ─── SEARCH & FILTER ─── */}
      <div className="bg-white rounded-xl border border-gray-200" data-id="element-14054">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-14055">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-14056">
            <SmartphoneIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-14057" />
            {t('pages.SoftPosTerminals.softpos_terminals')}
          </h2>
        </div>
        <div className="px-6 py-4 flex flex-col sm:flex-row gap-3" data-id="element-14058">
          <div className="relative flex-1" data-id="element-14059">
            <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-14060" />

            <input type="text" className={`${inputClass} pl-9`} placeholder={t('pages.SoftPosTerminals.attrs.search_by_device_mid_tid_or')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-14061" />
          </div>
          <div className="flex items-center gap-2" data-id="element-14062">
            <StatusDropdown value={statusFilter} onChange={setStatusFilter} options={[{
            value: 'all',
            label: 'All status'
          }, {
            value: 'active',
            label: 'Active',
            dot: 'bg-green-500'
          }, {
            value: 'inactive',
            label: 'Inactive',
            dot: 'bg-red-500'
          }, {
            value: 'pending',
            label: 'Pending',
            dot: 'bg-amber-500'
          }]} data-id="element-14063" />

            <button className="h-[42px] px-3 border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1.5" data-id="element-14064">
              <RefreshCwIcon size={13} data-id="element-14065" />
              {t('pages.Connect.refresh')}
            </button>
          </div>
        </div>
      </div>

      {/* ─── TERMINAL GRID ─── */}
      {filteredTerminals.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-id="element-14066">
          {filteredTerminals.map(terminal => <div key={terminal.id} className="bg-white rounded-xl border border-gray-200" data-id="element-14067">
              {/* Header */}
              <div className="px-5 py-3.5 border-b border-gray-200 flex items-center justify-between" data-id="element-14068">
                <div className="flex items-center gap-2.5 min-w-0" data-id="element-14069">
                  <div className="h-8 w-8 rounded-lg bg-[#E2231A]/10 flex items-center justify-center flex-shrink-0" data-id="element-14070">
                    <SmartphoneIcon size={14} className="text-[#9F140F]" data-id="element-14071" />
                  </div>
                  <div className="min-w-0" data-id="element-14072">
                    <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-14073">
                      {terminal.deviceName}
                    </p>
                    <p className="text-[11px] text-gray-500" data-id="element-14074">
                      {terminal.model} {t('pages.SoftPosTerminals.android')}{' '}
                      {terminal.androidVersion}
                    </p>
                  </div>
                </div>
                {getStatusBadge(terminal.status)}
              </div>

              {/* Details — edge-to-edge rows */}
              <div data-id="element-14075">
                <div className="flex justify-between items-center px-5 py-2.5 border-b border-gray-100" data-id="element-14076">
                  <span className="text-xs text-gray-500" data-id="element-14077">
                    {t('pages.SoftPosTerminals.owner')}
                  </span>
                  <span className="text-xs font-medium text-gray-900" data-id="element-14078">
                    {terminal.owner}
                  </span>
                </div>
                <div className="flex justify-between items-center px-5 py-2.5 border-b border-gray-100" data-id="element-14079">
                  <span className="text-xs text-gray-500" data-id="element-14080">
                    {t('pages.SoftPosTerminals.merchant_id')}
                  </span>
                  <span className="text-xs font-medium text-gray-900 font-mono" data-id="element-14081">
                    {terminal.merchantId}
                  </span>
                </div>
                <div className="flex justify-between items-center px-5 py-2.5 border-b border-gray-100" data-id="element-14082">
                  <span className="text-xs text-gray-500" data-id="element-14083">
                    {t('pages.SoftPosTerminals.terminal_id')}
                  </span>
                  <span className="text-xs font-medium text-gray-900 font-mono" data-id="element-14084">
                    {terminal.terminalId}
                  </span>
                </div>
                <div className="flex justify-between items-center px-5 py-2.5 border-b border-gray-100" data-id="element-14085">
                  <span className="text-xs text-gray-500" data-id="element-14086">
                    {t('pages.PhysicalTerminals.last_connection')}
                  </span>
                  <span className="text-xs font-medium text-gray-900" data-id="element-14087">
                    {terminal.lastConnection}
                  </span>
                </div>
                <div className="flex justify-between items-center px-5 py-2.5 border-b border-gray-100" data-id="element-14088">
                  <span className="text-xs text-gray-500" data-id="element-14089">
                    {t('pages.Connect.volume')}
                  </span>
                  <span className="text-xs font-medium text-gray-900" data-id="element-14090">
                    {getTenant().currency.code}{' '}
                    {terminal.transactionVolume.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center px-5 py-2.5 border-b border-gray-100" data-id="element-14091">
                  <span className="text-xs text-gray-500" data-id="element-14092">
                    {t('pages.Connect.transactions')}
                  </span>
                  <span className="text-xs font-medium text-gray-900" data-id="element-14093">
                    {terminal.transactionCount}
                  </span>
                </div>
                <div className="flex justify-between items-center px-5 py-2.5" data-id="element-14094">
                  <span className="text-xs text-gray-500" data-id="element-14095">
                    {t('pages.SoftPosTerminals.app_version')}
                  </span>
                  <span className="text-xs font-medium text-gray-900" data-id="element-14096">
                    {t('pages.PhysicalTerminals.v')}
                    {terminal.appVersion}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="px-5 py-3 border-t border-gray-200 flex justify-between" data-id="element-14097">
                <button className="text-xs font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors" data-id="element-14098">
                  {t('pages.PhysicalTerminals.view_details')}
                </button>
                <button className="text-xs font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors" data-id="element-14099">
                  {t('pages.SoftPosTerminals.update_app')}
                </button>
              </div>
            </div>)}
        </div> : <div className="bg-white rounded-xl border border-gray-200 py-16 flex flex-col items-center text-center" data-id="element-14100">
          <SmartphoneIcon size={24} className="text-gray-400 mb-3" data-id="element-14101" />
          <p className="text-sm font-medium text-gray-700" data-id="element-14102">
            {t('pages.SoftPosTerminals.no_softpos_terminals_found')}
          </p>
          <p className="text-xs text-gray-500 mt-1 max-w-xs" data-id="element-14103">
            {t('pages.SoftPosTerminals.no_terminals_match_your_search_criteria')}
          </p>
        </div>}
    </div>;
}